import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'
import * as shared from '../shared'

/**
 * Modal component for selecting occupants/employees for a space.
 */
@ngCore.Component({
  imports: [
    ds.layout.widget.Component,
    ds.overlay.centered.Component,
    ds.button.Component,
    shared.employeeSelector.Component,
  ],
  selector: 'atn-space-occupants-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** The occupants entity collection */
  public readonly ecSg = ngCore.input.required<
    | state.space.full.recordOccupants.Collection
    | state.space.full.clusterOccupants.Collection
  >({ alias: 'collection' })

  /** @see {@link state.space.full.employee.Collection} */
  public readonly employeesEc = ngCore.inject(
    state.space.full.employee.Collection,
  )

  /** List of employees that are currently not occupants */
  public readonly employeesSg = ngCore.computed(
    (
      allEmployees = this.employeesEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
      occupants = this.ecSg().plugins.cachePlugin.feature.selectors.lookup.asSignal(),
    ) =>
      allEmployees.filter(
        employee =>
          !Object.values(occupants).some(
            occupant => occupant?.employee.id === employee.id,
          ),
      ),
  )

  /** Employees currently checked in the modal — derived from selection entries */
  public readonly selectedEmployeesSg = ngCore.computed(
    (
      selectedEmployees = this.employeesEc.plugins.selectionPlugin.feature.selectors
        .getAllSelected({
          from: 'applied',
          target: this.ecSg().associations?.selectionTarget,
        })
        .asSignal(),
    ) => selectedEmployees.filter(TypeChecker.isNotNullish.raw),
  )

  /** Handles checkbox toggles — adds/removes the employee from the selection */
  public employeeCheckboxChangedHandler = (
    id: ents.extra.space.Employee.Id,
    selected: boolean,
  ) => {
    this.employeesEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from('Occupants.Modal.EmployeeCheckbox.Toggled')
      .dispatch({
        key: this.ecSg().associations?.selectionTarget,
        value: selected ? { add: id } : { remove: id },
      })
  }

  /** Removes an employee from the selection (tag close button) */
  public removeSelectedEmployee = (
    employeeId: ents.extra.space.Employee.Id,
  ): void => {
    this.employeesEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from('Occupants.Modal.SelectedTag.Removed')
      .dispatch({
        key: this.ecSg().associations?.selectionTarget,
        value: { remove: employeeId },
      })
  }

  /** Save changes handler */
  public saveClickHandler = (): void => {
    this.ecSg()
      .plugins.editingPlugin.feature.actions.submit.from(
        'Occupants.Modal.SaveButton.Clicked',
      )
      .dispatch({ create: true })
  }

  /** Discard changes handler */
  public cancelClickHandler = (): void => {
    this.ecSg()
      .plugins.editingPlugin.feature.actions.discard.from(
        'Occupants.Modal.CancelButton.Clicked',
      )
      .dispatch({ create: true })
  }
}
